// src/components/SafetyWatch/ActivityRiskCard.js
// Renders a single activity's risk assessment with full transparency:
//   - Overall risk badge
//   - Expandable factor-by-factor breakdown showing value, threshold, and explanation

import React, { useState } from "react";
import { riskLevelMeta, RISK_LEVELS } from "../../constants/activityThresholds";

const RiskBadge = ({ level, size = "md" }) => {
  const meta = riskLevelMeta[level] || riskLevelMeta.green;
  const sizeClasses = size === "lg"
    ? "px-3 py-1.5 text-sm font-bold"
    : "px-2 py-0.5 text-xs font-semibold";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${sizeClasses}`}
      style={{ background: meta.bg, color: meta.color, border: `1.5px solid ${meta.border}` }}
    >
      {meta.emoji} {meta.label}
    </span>
  );
};

const FactorRow = ({ factorKey, factor }) => {
  const meta = riskLevelMeta[factor.level] || riskLevelMeta.green;
  return (
    <div
      className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 w-full"
    >
      {/* Top row: indicator and factor name */}
      <div className="flex items-center gap-2 mb-1">
        <span
          className="inline-block w-3 h-3 rounded-full flex-shrink-0"
          style={{ background: meta.color }}
          title={factor.level}
        />
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {factor.label}
        </span>
      </div>

      {/* Current value - on its own line if long */}
      <div className="pl-5 ml-2 mb-1">
        <span 
          className="text-sm font-mono text-gray-600 dark:text-gray-400"
          style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
        >
          {factor.value ?? "N/A"}
        </span>
      </div>

      {/* Explanation on its own line for better readability */}
      <div className="pl-5 ml-2 border-l-2 border-gray-200 dark:border-gray-600" style={{ maxWidth: "100%", overflow: "hidden" }}>
        <p 
          className="text-xs text-gray-500 dark:text-gray-400 italic leading-relaxed m-0"
          style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}
        >
          {factor.explain}
        </p>
      </div>
    </div>
  );
};

const ActivityRiskCard = ({ activityKey, assessment }) => {
  const [expanded, setExpanded] = useState(false);
  const { label, overall, factors } = assessment;
  const meta = riskLevelMeta[overall] || riskLevelMeta.green;

  // Sort factors: worst risk first
  const sortedFactors = Object.entries(factors).sort((a, b) => {
    return RISK_LEVELS.indexOf(b[1].level) - RISK_LEVELS.indexOf(a[1].level);
  });

  // Count of factors at each risk level
  const counts = { green: 0, yellow: 0, orange: 0, red: 0 };
  sortedFactors.forEach(([, f]) => { counts[f.level] = (counts[f.level] || 0) + 1; });

  return (
    <div
      className="rounded-xl border-2 shadow-sm transition-all hover:shadow-md overflow-hidden"
      style={{ borderColor: meta.border, background: meta.bg }}
    >
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-base font-bold" style={{ color: meta.color, fontFamily: "Times New Roman, Times, serif", letterSpacing: "0.5px" }}>{label}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <RiskBadge level={overall} size="lg" />
              {/* Mini factor summary dots */}
              <span className="flex gap-0.5 ml-1">
                {sortedFactors.map(([fk, f]) => (
                  <span
                    key={fk}
                    className="w-2 h-2 rounded-full"
                    style={{ background: (riskLevelMeta[f.level] || riskLevelMeta.green).color }}
                    title={`${f.label}: ${f.level}`}
                  />
                ))}
              </span>
            </div>
          </div>
        </div>

        {/* Expand/collapse indicator */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
            {expanded ? "Hide details" : "Show how this is calculated"}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded detail — factor breakdown */}
      {expanded && (
        <div className="px-4 pb-4 pt-1">
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-semibold">Factor breakdown:</span>
            {RISK_LEVELS.map((lvl) => (
              <span key={lvl} className="flex items-center gap-1">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: riskLevelMeta[lvl].color }}
                />
                {riskLevelMeta[lvl].label}
                {counts[lvl] > 0 && (
                  <span className="font-mono">({counts[lvl]})</span>
                )}
              </span>
            ))}
          </div>

          {/* Factor rows */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 overflow-hidden">
            {sortedFactors.map(([fk, f]) => (
              <FactorRow key={fk} factorKey={fk} factor={f} />
            ))}
          </div>

          {/* Transparency note */}
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500 italic">
            Overall risk = highest risk factor. Thresholds are defined in{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-[10px]">
              activityThresholds.js
            </code>{" "}
            and can be customized.
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityRiskCard;
